import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { LumaService } from '../luma.service';
import { Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CustomerSearchDialogComponent } from 'src/app/shared/dialogs/customer-search-dialog/customer-search-dialog.component';
import { SessionService } from 'src/app/session.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaHandlerService } from '../../handlers/media-handler.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-luma-campaign-create',
  templateUrl: './campaign-create.component.html',
  styleUrls: ['./campaign-create.component.scss']
})
export class CampaignCreateComponent {
  step: 1 | 2 = 1;
  editId: string | null = null;
  editLocked = false;
  private selectedSubjectFieldId: string | null = null;

  step1Form = this.fb.group({
    name: ['', Validators.required],
    audienceMode: ['all', Validators.required],
    savedSearchId: [null],
    searchQuery: [''],
  });

  step2Form = this.fb.group({
    prompt: ['', Validators.required],
    subject: [''],
    generateSubject: ['no'],
    subjectField: [null],
    bodyFields: [[] as string[]],
  });

  preview: { subject?: string; body?: string } | null = null;
  logoUrl: string | null = null;
  audienceCount: number | null = null;
  isGenerating = false;

  profileFields: any[] = [];
  savedSearches: Array<{ id: number | string; name: string }> = [];

  constructor(
    private fb: FormBuilder,
    @Inject(LumaService) private luma: LumaService,
    private dialog: MatDialog,
    private session: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private mediaHandler: MediaHandlerService,
  ) {}

  onAudienceChanged(): void {
    const value = this.step1Form.value;
    this.luma.countAudience(value).subscribe((count: number) => this.audienceCount = count);
  }

  ngOnInit(): void {
    // Default audience is 'all' – fetch initial count on load
    this.onAudienceChanged();
    this.loadPreviewLogo();
    // Preload saved searches for selector display
    (this.luma as any).customerSearch.listSavedSearches().subscribe((res: any) => {
      this.savedSearches = res?.records || res || [];
    });

    // Edit mode: if :id present, load campaign and populate
    const editId = this.route.snapshot.paramMap.get('id');
    if (editId) {
      this.editId = editId;
      this.luma.getCampaign(editId).subscribe((c: any) => {
        this.editLocked = c.status === 'pending_send' || c.status === 'send_complete';
        // Populate forms
        if (c.name) { this.step1Form.controls.name.setValue(c.name); }
        if (c.audience) {
          const a = c.audience;
          if (a.mode === 'all') { this.step1Form.patchValue({ audienceMode: 'all' }); }
          if (a.mode === 'saved' && a.savedSearchId) { this.step1Form.patchValue({ audienceMode: 'saved', savedSearchId: a.savedSearchId }); }
          if (a.mode === 'search' && a.searchQuery) { this.step1Form.patchValue({ audienceMode: 'search', searchQuery: a.searchQuery }); }
        }
        if (c.prompt_config) {
          const p = c.prompt_config;
          this.step2Form.patchValue({
            prompt: p.prompt || '',
            subject: p.subject || '',
            generateSubject: p.generateSubject ? 'yes' : 'no',
            subjectField: p.subjectField || null,
            bodyFields: Array.isArray(p.bodyFields) ? p.bodyFields : []
          });
        }
        this.onAudienceChanged();
        this.applySubjectFieldValidators();
        this.refreshBodyFieldState();
        if (this.editLocked) {
          this.step1Form.disable();
          this.step2Form.disable();
        }
      });
    }

    // If navigating here with duplicateFrom, prefill forms
    const nav = window.history.state as any;
    if (nav && nav.duplicateFrom) {
      const src = nav.duplicateFrom;
      if (src.name) { this.step1Form.controls.name.setValue(src.name + ' (Copy)'); }
      if (src.audience) {
        const a = src.audience;
        if (a.mode === 'all') { this.step1Form.patchValue({ audienceMode: 'all' }); }
        if (a.mode === 'saved' && a.savedSearchId) { this.step1Form.patchValue({ audienceMode: 'saved', savedSearchId: a.savedSearchId }); }
        if (a.mode === 'search' && a.searchQuery) { this.step1Form.patchValue({ audienceMode: 'search', searchQuery: a.searchQuery }); }
      }
      if (src.prompt_config) {
        const p = src.prompt_config;
        this.step2Form.patchValue({
          prompt: p.prompt || '',
          subject: p.subject || '',
          generateSubject: p.generateSubject ? 'yes' : 'no',
          subjectField: p.subjectField || null,
          bodyFields: Array.isArray(p.bodyFields) ? p.bodyFields : []
        });
      }
      this.onAudienceChanged();
      this.applySubjectFieldValidators();
      this.refreshBodyFieldState();
    }

    this.applySubjectFieldValidators();
    this.refreshBodyFieldState();
  }

  openCustomerSearch(): void {
    const formVal: any = this.step1Form.value;
    const accountId = this.session.currentAccountValue!.accountID;
    const appId = this.session.currentAppValue!.id;

    const dialogData: any = {
      context: { account_id: accountId, account_app_id: appId }
    };

    if (formVal.audienceMode === 'saved' && formVal.savedSearchId) {
      dialogData.selectedSavedId = formVal.savedSearchId;
    } else if (formVal.audienceMode === 'search' && formVal.searchQuery) {
      dialogData.initialQuery = formVal.searchQuery.query || null;
      dialogData.context = { ...(formVal.searchQuery.context || {}), account_id: accountId, account_app_id: appId };
    } else if (formVal.audienceMode === 'all') {
      // Align dialog defaults with "All Customers" = registered users only
      dialogData.context = {
        ...(dialogData.context || {}),
        mode: { registration: 'registered', devices: 'latest_per_user' }
      };
    }

    const ref = this.dialog.open(CustomerSearchDialogComponent, { width: '100%', data: { ...dialogData, forceRegisteredOnly: true, requireEmail: true } });
    ref.afterClosed().subscribe((result: any) => {
      if (!result) { return; }
      if (result.type === 'search' && result.payload) {
        this.step1Form.patchValue({ audienceMode: 'search', searchQuery: result.payload });
        // Ensure context is present for count endpoint
        const accId = (this.luma as any).session?.currentAccountValue?.accountID;
        const appId = (this.luma as any).session?.currentAppValue?.id;
        result.payload.context = result.payload.context || {};
        (result.payload.context as any).account_id = accId;
        (result.payload.context as any).account_app_id = appId;
        this.onAudienceChanged();
      } else if (result.type === 'saved' && result.id) {
        this.step1Form.patchValue({ audienceMode: 'saved', savedSearchId: result.id });
        // Ensure selector list contains the selected saved search and is selected
        const exists = this.savedSearches.find(s => s.id === result.id);
        if (!exists) {
          (this.luma as any).customerSearch.listSavedSearches().subscribe((res: any) => {
            this.savedSearches = res?.records || res || [];
          });
        }
        this.onAudienceChanged();
      }
    });
  }

  onSavedSearchSelected(id: number) {
    if (!id) { return; }
    this.step1Form.patchValue({ savedSearchId: id, audienceMode: 'saved' });
    this.onAudienceChanged();
  }

  continue(): void {
    if (this.step1Form.valid) {
      this.step = 2;
      // Match POC: use account catalog profile fields
      this.profileFields = this.session.currentAccountValue!.catalog.app_users.profile;
    }
  }

  generatePreview(): void {
    if (this.disableGeneratePreview()) return;
    this.isGenerating = true;
    this.preview = null;
    const v = this.step2Form.value as any;
    const payload = {
      generateSubject: v.generateSubject === 'yes',
      subjectField: v.subjectField,
      subject: v.generateSubject === 'yes' ? null : v.subject,
      bodyFields: v.bodyFields,
      prompt: v.prompt
    };
    this.luma.generatePreview(payload).subscribe({
      next: (p: { subject?: string; body?: string }) => { this.preview = p; this.isGenerating = false; },
      error: () => { this.isGenerating = false; }
    });
  }

  generateSubjectChange(): void {
    const previousSubject = this.selectedSubjectFieldId;
    const subjectFieldControl = this.step2Form.controls.subjectField;

    subjectFieldControl.setValue(null);

    this.applySubjectFieldValidators();

    const currentBody = Array.isArray(this.step2Form.controls.bodyFields.value)
      ? [...this.step2Form.controls.bodyFields.value as string[]]
      : [];
    const filteredBody = previousSubject
      ? currentBody.filter(id => id !== previousSubject)
      : currentBody;

    this.selectedSubjectFieldId = null;
    this.refreshBodyFieldState(filteredBody);
  }

  subjectFieldChange(fieldId: string): void {
    const previousSubject = this.selectedSubjectFieldId;
    this.selectedSubjectFieldId = fieldId;
    const currentBody = Array.isArray(this.step2Form.controls.bodyFields.value)
      ? [...this.step2Form.controls.bodyFields.value as string[]]
      : [];
    const filteredBody = previousSubject
      ? currentBody.filter(id => id !== previousSubject)
      : currentBody;
    this.refreshBodyFieldState(filteredBody);
  }

  bodyFieldClick(fieldId: string): void {
    const bodyControl = this.step2Form.controls.bodyFields;
    const generateYes = this.step2Form.controls.generateSubject.value === 'yes';
    const subjectField = this.step2Form.controls.subjectField.value as string | null;
    const currentBody = Array.isArray(bodyControl.value) ? [...bodyControl.value as string[]] : [];

    if (generateYes && subjectField && fieldId === subjectField) { return; }
    if (currentBody.includes(fieldId)) { return; }

    const additionalLimit = generateYes && subjectField ? 2 : 3;
    const additionalSelected = generateYes && subjectField
      ? currentBody.filter(id => id !== subjectField)
      : currentBody;

    if (additionalSelected.length >= additionalLimit) { return; }

    this.refreshBodyFieldState([...currentBody, fieldId]);
  }

  highlightBodyField(fieldId: string): boolean {
    const body = Array.isArray(this.step2Form.controls.bodyFields.value)
      ? this.step2Form.controls.bodyFields.value as string[]
      : [];
    return body.indexOf(fieldId) > -1;
  }

  removableBodyField(fieldId: string): boolean {
    const body = Array.isArray(this.step2Form.controls.bodyFields.value)
      ? this.step2Form.controls.bodyFields.value as string[]
      : [];
    const generateYes = this.step2Form.controls.generateSubject.value === 'yes';
    const subjectField = this.step2Form.controls.subjectField.value as string | null;
    if (generateYes && subjectField === fieldId) { return false; }
    return body.indexOf(fieldId) > -1;
  }

  removeBodyField(fieldId: string): void {
    const generateYes = this.step2Form.controls.generateSubject.value === 'yes';
    const subjectField = this.step2Form.controls.subjectField.value as string | null;
    if (generateYes && subjectField === fieldId) { return; }

    const bodyControl = this.step2Form.controls.bodyFields;
    const currentBody = Array.isArray(bodyControl.value) ? [...bodyControl.value as string[]] : [];
    const idx = currentBody.indexOf(fieldId);
    if (idx === -1) { return; }
    currentBody.splice(idx, 1);
    this.refreshBodyFieldState(currentBody);
  }

  disableGeneratePreview(): boolean {
    const v = this.step2Form.value as any;
    if (!v.prompt || v.prompt.trim() === '') return true;
    if (v.generateSubject === 'yes' && !v.subjectField) return true;
    if (v.generateSubject === 'no' && (!v.subject || v.subject.trim() === '')) return true;
    if (v.bodyFields.length === 0) return true;
    return false;
  }

  saveCampaign(): void {
    if (this.step1Form.invalid || this.step2Form.invalid) return;
    const s1: any = this.step1Form.value;
    const s2: any = this.step2Form.value;

    // Build audience payload expected by API
    const audience: any = { mode: s1.audienceMode };
    if (s1.audienceMode === 'saved' && s1.savedSearchId) {
      audience.savedSearchId = s1.savedSearchId;
    }
    if (s1.audienceMode === 'search' && s1.searchQuery) {
      audience.searchQuery = s1.searchQuery;
    }

    const payload: any = {
      name: s1.name,
      audience,
      ...s2,
    };
    if (this.editId) {
      if (this.editLocked) return; // safety
      this.luma.updateCampaign(this.editId, payload).subscribe({
        next: () => this.router.navigate(['/luma', this.editId!])
      });
    } else {
      this.luma.createCampaign(payload).subscribe({
        next: () => this.router.navigate(['/luma/campaigns']),
      });
    }
  }

  private applySubjectFieldValidators(): void {
    const subjectFieldControl = this.step2Form.controls.subjectField;
    const generateYes = this.step2Form.controls.generateSubject.value === 'yes';
    if (generateYes) {
      subjectFieldControl.setValidators([Validators.required]);
    } else {
      subjectFieldControl.clearValidators();
    }
    subjectFieldControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
  }

  private refreshBodyFieldState(nextBodyFields?: string[]): void {
    const bodyControl = this.step2Form.controls.bodyFields;
    const generateYes = this.step2Form.controls.generateSubject.value === 'yes';
    const subjectField = this.step2Form.controls.subjectField.value as string | null;
    const currentControlValue = Array.isArray(bodyControl.value)
      ? bodyControl.value as string[]
      : [];

    let body = Array.isArray(nextBodyFields)
      ? [...nextBodyFields]
      : Array.isArray(bodyControl.value)
      ? [...bodyControl.value as string[]]
      : [];

    body = body.filter((id, index) => body.indexOf(id) === index);

    if (generateYes && subjectField) {
      body = [subjectField, ...body.filter(id => id !== subjectField)];
    } else if (!generateYes && subjectField) {
      body = body.filter(id => id !== subjectField);
    }

    if (body.length > 3) {
      body = body.slice(0, 3);
    }

    if (!this.arraysEqual(body, currentControlValue)) {
      bodyControl.setValue(body, { emitEvent: false });
    }

    const additionalCount = generateYes && subjectField
      ? body.filter(id => id !== subjectField).length
      : body.length;
    const additionalLimit = generateYes && subjectField ? 2 : 3;

    const errors: Record<string, boolean> = {};
    if (!generateYes && additionalCount === 0) {
      errors.bodyFieldsMin = true;
    }
    if (additionalCount > additionalLimit) {
      errors.bodyFieldsMax = true;
    }

    bodyControl.setErrors(Object.keys(errors).length ? errors : null);
    bodyControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    this.selectedSubjectFieldId = subjectField;
  }

  private arraysEqual(a: string[] = [], b: string[] = []): boolean {
    if (a.length !== b.length) { return false; }
    return a.every((val, index) => val === b[index]);
  }

  private loadPreviewLogo(): void {
    const accountId = this.session.currentAccountValue?.accountID;
    if (!accountId) {
      this.logoUrl = null;
      return;
    }

    this.mediaHandler
      .getAll(accountId, {
        page: 0,
        perPage: 1,
        context: 'luma_preview_logo',
      })
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const records = Array.isArray(response?.records)
            ? response.records
            : Array.isArray(response)
            ? response
            : [];
          this.logoUrl = records.length ? records[0]?.url || null : null;
        },
        error: () => {
          this.logoUrl = null;
        },
      });
  }
}


